import { getAccessTokenSilentlyOutsideComponent } from '@/lib/auth'

/**
 * Known category slugs. The authoritative taxonomy lives in the API's
 * `support_note_categories` lookup table and is fetched via `listNoteCategories()`;
 * this union mirrors the seeded slugs for compile-time ergonomics. Treat the API as the
 * source of truth — a slug outside this list that the server returns is still valid.
 */
export type SupportNoteCategory =
    | 'general'
    | 'fyi'
    | 'troubleshooting'
    | 'known-issue'
    | 'account-access'
    | 'subscription-promo'
    | 'privacy-compliance'
    | 'intouch'
    | 'hardware'
    | 'smart-routine'
    | 'watercare'
    | 'waterlab'

export interface SupportNoteV1 {
    id: string
    accountId: number
    vesselId: number | null
    authorId: string
    authorName: string
    authorInitials: string
    category: SupportNoteCategory
    /** Human-readable label for `category`, resolved server-side from the lookup table. */
    categoryLabel: string
    body: string
    pinned: boolean
    createdAt: string
    updatedAt: string
}

/** A selectable category returned by `GET /v1/note-categories` (active categories only). */
export interface SupportNoteCategoryV1 {
    slug: SupportNoteCategory
    label: string
    sortOrder: number
}

export interface ListNoteCategoriesResponse {
    categories: SupportNoteCategoryV1[]
}

export interface ListNotesResponse {
    notes: SupportNoteV1[]
    nextCursor: string | null
    total: number
    limit: number
}

export interface ListNotesParams {
    vesselId?: number | null
    category?: SupportNoteCategory
    pinned?: boolean
    q?: string
    limit?: number
    cursor?: string
}

export interface CreateNoteBody {
    vesselId?: number | null
    category: SupportNoteCategory
    body: string
    pinned?: boolean
}

export interface UpdateNoteBody {
    body?: string
    category?: SupportNoteCategory
    pinned?: boolean
}

/**
 * Error thrown when the support notes API returns a non-2xx response. Surfaces
 * the server-provided `error` message and any per-field messages so callers can
 * present a meaningful toast.
 */
export class SupportNotesApiError extends Error {
    status: number
    fields?: Record<string, string>

    constructor(status: number, message: string, fields?: Record<string, string>) {
        super(message)
        this.name = 'SupportNotesApiError'
        this.status = status
        this.fields = fields
    }
}

function baseUrl(): string {
    return import.meta.env.VITE_CRYSTAL_API_BASE_URL
}

async function authHeaders(): Promise<Record<string, string>> {
    const token = await getAccessTokenSilentlyOutsideComponent()
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
    }
}

async function parseResponse<T>(response: Response): Promise<T> {
    let json: unknown
    try {
        json = await response.json()
    } catch (_err) {
        json = undefined
    }

    if (!response.ok) {
        const payload = (json ?? {}) as { error?: string; message?: string; fields?: Record<string, string> }
        const message = payload.error ?? payload.message ?? `Request failed with status ${response.status}`
        throw new SupportNotesApiError(response.status, message, payload.fields)
    }

    return json as T
}

/**
 * Fetches the active note categories (the taxonomy) from the API. Used to build the
 * category picker and filter chips so the admin stays in sync with the server-managed
 * taxonomy without a code change when categories are added, relabeled or deprecated.
 */
export async function listNoteCategories(): Promise<SupportNoteCategoryV1[]> {
    const url = `${baseUrl()}/v1/note-categories`
    const response = await fetch(url, { method: 'GET', headers: await authHeaders() })
    const parsed = await parseResponse<ListNoteCategoriesResponse>(response)
    return parsed.categories
}

export async function listNotes(accountId: number, params: ListNotesParams = {}): Promise<ListNotesResponse> {
    const query = new URLSearchParams()
    if (params.vesselId !== undefined && params.vesselId !== null) query.set('vesselId', String(params.vesselId))
    if (params.category !== undefined) query.set('category', params.category)
    if (params.pinned !== undefined) query.set('pinned', String(params.pinned))
    if (params.q !== undefined && params.q !== '') query.set('q', params.q)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.cursor !== undefined) query.set('cursor', params.cursor)

    const qs = query.toString()
    const url = `${baseUrl()}/v1/accounts/${accountId}/notes${qs ? `?${qs}` : ''}`
    const response = await fetch(url, { method: 'GET', headers: await authHeaders() })
    return parseResponse<ListNotesResponse>(response)
}

export async function createNote(accountId: number, body: CreateNoteBody): Promise<SupportNoteV1> {
    const url = `${baseUrl()}/v1/accounts/${accountId}/notes`
    const response = await fetch(url, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(body)
    })
    return parseResponse<SupportNoteV1>(response)
}

export async function updateNote(noteId: string, body: UpdateNoteBody): Promise<SupportNoteV1> {
    const url = `${baseUrl()}/v1/notes/${noteId}`
    const response = await fetch(url, {
        method: 'PATCH',
        headers: await authHeaders(),
        body: JSON.stringify(body)
    })
    return parseResponse<SupportNoteV1>(response)
}

export async function deleteNote(noteId: string): Promise<{ success: boolean }> {
    const url = `${baseUrl()}/v1/notes/${noteId}`
    const response = await fetch(url, { method: 'DELETE', headers: await authHeaders() })
    return parseResponse<{ success: boolean }>(response)
}
